import { Match, Switch, createEffect, createSignal, onCleanup, onMount } from "solid-js";
import PhotoSwipeLightbox from "../../libs/js/photoswipe-lightbox.esm.min.js";
import ObjectPosition from "../../libs/js/photoswipe-object-position.js";
import { HorizontalScrollerGradient } from "../components/HorizontalScroller.jsx";

const getGridSpan = (index, length) => {
  const isFirst = index === 0;
  const isLast = index === length - 1;

  const mod2 = length % 2;
  const reverseIndex = length - (index + 1);

  if (isFirst) {
    return 'col-span-2'
  } else if (isLast) {
    if (mod2 == 0) {
      return 'col-span-2'
    } else {
      return 'col-span-2 lg:col-span-1'
    }
  } else if (reverseIndex == 1) {
    if (mod2 == 0) {
      return 'col-span-2 lg:col-span-1'
    } else {
      return 'col-span-2 lg:col-span-1'
    }
  } else {
    return 'col-span-2 lg:col-span-1'
  }
}

// Must match snippets/hero-lcp-image.liquid so the Solid-rendered first slide of the
// selected colour resolves to the same srcset candidate as the server-rendered LCP image.
const LCP_SRCSET_WIDTHS = [462, 614, 768, 924, 1228, 1400, 1535, 1842, 2304, 2760];
const LCP_SIZES = '(min-width: 640px) 50vw, 62vw'; // 62vw = DPR-capped mobile candidate, see hero-lcp-image.liquid
const LCP_FALLBACK_WIDTH = 768;
const LQIP_WIDTH = 32;

const lcpConfig = () => ({
  widths: window.__heroLcp?.widths ?? LCP_SRCSET_WIDTHS,
  sizes: window.__heroLcp?.sizes ?? LCP_SIZES,
});

const buildSrcset = (src, widths) => widths.map((width) => `${src}&width=${width} ${width}w`).join(', ');

export const HeroImageGallerySwiper = (props) => {
  const {
    color: {
      color
    },
    getters: {
      getExperimentFlags
    },
    heroSectionSettings
  } = props.context();

  let variantGalleryRef,
    prevBtnRef,
    nextBtnRef,
    firstSlideObserver,
    lastSlideObserver,
    videoSlideObserver;

  const colorOptions = () => Object.values(color().options)

  const isColorSelected = (id) => id === color().selected;

  const [prevBtnVisibility, setPrevBtnVisibility] = createSignal(false);
  const [nextBtnVisibility, setNextBtnVisibility] = createSignal(true);

  // PhotoSwipe is initialised once: it delegates clicks on `.subvariant-swiper a`, so it already
  // covers every colour's gallery. It used to be re-created inside the colour effect, which
  // stacked a new lightbox (and its listeners) on every swatch tap — ~200ms of the tap's INP.
  onMount(() => {
    const lightbox = new PhotoSwipeLightbox({
      gallery: '.subvariant-swiper',
      children: 'a',
      initialZoomLevel: 'fit',
      tapAction: 'zoom',
      imageClickAction: 'zoom',
      pswpModule: () => import("../../libs/js/photoswipe.esm.min.js"),
    });
    new ObjectPosition(lightbox);
    lightbox.init();
    onCleanup(() => lightbox.destroy());
  });

  const anyIntersecting = (entries) => entries.some((entry) => entry.isIntersecting);

  // Only the selected colour's gallery is visible, so only its edge slides and videos are
  // observed; the other colours' galleries are `hidden` and never intersect anyway.
  createEffect(() => {
    const selectedGallery = variantGalleryRef?.querySelector(`[data-hash="${color().selected}"]`);
    if (!selectedGallery || selectedGallery.childElementCount === 0) return;

    const firstSlide = selectedGallery.children[0];
    const lastSlide = selectedGallery.children[selectedGallery.childElementCount - 1];
    const videoSlides = selectedGallery.querySelectorAll('video');

    firstSlideObserver = new IntersectionObserver((entries) => {
      const intersectionRatio = entries.reduce((sum, entry) => sum + entry.intersectionRatio, 0);
      if (intersectionRatio > 0) setPrevBtnVisibility(!anyIntersecting(entries));
    }, { threshold: [0.1, 0.2, 0.5, 0.9] });

    lastSlideObserver = new IntersectionObserver((entries) => {
      setNextBtnVisibility(!anyIntersecting(entries));
    }, { threshold: [0.5] });

    videoSlideObserver = new IntersectionObserver((entries) => {
      try {
        for (const { target, isIntersecting } of entries) {
          if (isIntersecting) {
            target.play();
          } else {
            target.pause();
          }
        }
      } catch (error) {
        console.error("Error in videoSlideObserver. entries: ", entries);
      }
    }, { threshold: [0.5] });

    firstSlideObserver.observe(firstSlide);
    lastSlideObserver.observe(lastSlide);
    videoSlides.forEach((video) => videoSlideObserver.observe(video));

    onCleanup(() => {
      firstSlideObserver.disconnect();
      lastSlideObserver.disconnect();
      videoSlideObserver.disconnect();
    });

    setPrevBtnVisibility(false);
    setNextBtnVisibility(true);
  });

  const variantScroll = (direction) => {
    window.posthog?.capture('gallery_swipe_btn_click', {
      experiment_flags: window.experimentFlags,
      direction
    })

    let scrollingDiv = variantGalleryRef.parentElement.getElementsByClassName('active');
    const imageWidth = scrollingDiv[0].clientWidth;
    const currentScrollPosition = scrollingDiv[0].scrollLeft;

    if (scrollingDiv) {
      if (direction === 'left') {
        const nextImagePosition = currentScrollPosition - imageWidth;
        scrollingDiv[0].scrollLeft = nextImagePosition;
      }
      else {
        if (nextBtnVisibility()) {
          const nextImagePosition = currentScrollPosition + imageWidth;
          scrollingDiv[0].scrollLeft = nextImagePosition;
        }
        else {
          scrollingDiv[0].scrollLeft = 0;
        }
      }
    }
  }

  return (
    <div
      id="image-gallery"
      class={`flex flex-col select-none justify-between relative sm:landscape:w-[calc(80vh-3.2rem)] 2xl:landscape:w-[calc(80vh-4rem)] flex-shrink-0  ${props?.isModal ? "" : "sm:landscape:border-l-outer border-frame"
        }`}
    >
      <div
        ref={variantGalleryRef}
        class={`relative overflow-x-visible sm:landscape:h-[calc(100vh-4rem)] 2xl:landscape:h-[calc(100vh-5rem)] lg:portrait:h-[calc(100vh-4rem)] 2xl:portrait:h-[calc(100vh-5rem)] sm:sticky sm:top-0 lg:top-6 2xl:top-10 ${props?.isModal ? "" : "sm:portrait:h-[100vw]"
          }`}
      >
        <For each={colorOptions()}>
          {(colorOption, index) => {
            const galleryImages = () => colorOption.galleryImages;
            const isSelected = () => isColorSelected(colorOption.id);
            const galleryMedia = () => colorOption.media;

            return (
              <div
                // style="scroll-behavior: smooth;"
                id={`${colorOption.id}-slides`}
                class={`subvariant-swiper flex-row items-stretch overflow-x-scroll scroll-smooth snap-x snap-mandatory w-full h-full GA-element-observer sm:ml-negative-outer ${props?.isModal == true // scrollbar-hide  
                  ? "landscape:!flex-col lg:!flex-col"
                  : "border-b-outer sm:border-b-0 border-frame"
                  }`}
                classList={{
                  "pdp-gallery": heroSectionSettings.isNewVersion,
                  hidden: !isColorSelected(colorOption.id),
                  "active flex": isColorSelected(colorOption.id),
                }}
                // style="scroll-snap-points-x: repeat(100vw)"
                data-element-meta={`${template} / Product hero Classic Flat / ${colorOption.label}`}
                data-hash={colorOption.id}
              >
                <For each={galleryMedia()}>
                  {(media, index) => {
                    const gridSpan = getGridSpan(
                      index(),
                      galleryMedia().length
                    );
                    const isLcpSlide = () => isSelected() && index() === 0;

                    return (
                      <Switch>
                        <Match when={media.type == "image"}>
                          <a
                            id={`${colorOption.id}-slide-${index()}`}
                            href={media.src}
                            class={`${gridSpan} block relative cursor-zoom-in shrink-0  snap-start snap-always scroll-m-0 scroll-p-0 ${props?.isModal
                              ? "pr-0.5 lg:pr-0 lg:pb-3 landscape:pb-1 landscape:pr-0"
                              : "dark:border-secondary-200  border-frame sm:border-b-0 border-l-outer first:border-l-0 h-[125vw] sm:h-full"
                              }`}
                            classList={{
                              "w-11/12": heroSectionSettings.isNewVersion,
                              "w-screen sm:min-w-full sm:w-full": !heroSectionSettings.isNewVersion
                            }}
                            data-pswp-src={media.src}
                            data-pswp-width={media.width}
                            data-pswp-height={media.height}
                          >
                            <Show when={heroSectionSettings.isNewVersion && colorOption.useAltOverlay && media.altText}>
                              <div
                                class="absolute bg-canvas-depressed bottom-2 right-2 px-1 py-0.5 text-2xs w-fit text-wrap max-w-35 overflow-hidden text-ellipsis"
                              >{media.altText}</div>
                            </Show>
                            <img
                              class={`${gridSpan} object-cover object-center ${props?.isModal
                                ? "h-30 sm:h-55 lg:h-[75vh]"
                                : "h-[125vw] min-w-full"
                                } w-auto sm:w-full sm:h-full text-transparent  light-gallery cursor-zoom-in border-frame dark:border-secondary-200 `}
                              classList={{
                                hidden: !isSelected(),
                                // first slide of the selected colour is the LCP: native srcset, no lazysizes.
                                // Hidden colours are not tracked by lazysizes at all (it re-scans every
                                // .lazyload element on scroll/resize/mutation); the class is added on switch.
                                lazyload: isSelected() && !isLcpSlide(),
                                // only pre-warm the selected colour; hidden colours load on switch
                                lazypreload: isSelected() && !isLcpSlide() && index() < 3,
                              }}
                              style={`aspect-ratio: ${media.aspectRatio}`}
                              src={
                                isLcpSlide()
                                  ? `${media.src}&width=${LCP_FALLBACK_WIDTH}`
                                  : isSelected()
                                    ? `${media.src}&width=${LQIP_WIDTH}`
                                    : undefined
                              }
                              srcset={isLcpSlide() ? buildSrcset(media.src, lcpConfig().widths) : undefined}
                              sizes={isLcpSlide() ? lcpConfig().sizes : undefined}
                              data-aspectratio={media.aspectRatio}
                              data-width={media.width}
                              // same `sizes` as the LCP slide: `auto` resolved to the old lazysizes 1.6x cap and
                              // fetched 614w where the LCP slide in the identical slot fetched 462w
                              data-sizes={isLcpSlide() ? undefined : lcpConfig().sizes}
                              alt={media.alt}
                              data-srcset={isLcpSlide() ? undefined : buildSrcset(media.src, LCP_SRCSET_WIDTHS)}
                              height="auto"
                              width="auto"
                              fetchpriority={isLcpSlide() ? "high" : "auto"}
                              loading={isSelected() && index() < 6 ? "eager" : "lazy"}
                              decoding="async"
                            ></img>
                          </a>
                        </Match>
                        <Match when={media.type == "video"}>
                          <div
                            id={`${colorOption.id}-slide-${index()}`}
                            href={
                              media.sources.find(
                                (source) => source.format == "mp4"
                              ).src
                            }
                            class={`${gridSpan} block border-frame cursor-zoom-in h-[125vw] shrink-0 sm:h-full  snap-start snap-always scroll-m-0 scroll-p-0 dark:border-secondary-200 border-b-outer sm:border-b-0 border-l-outer first:border-l-0`}
                            classList={{
                              "w-11/12": heroSectionSettings.isNewVersion,
                              "w-screen sm:min-w-full sm:w-full": !heroSectionSettings.isNewVersion
                            }}
                          >
                            <div
                              class={`${gridSpan} object-cover object-center h-[125vw] min-w-full w-auto sm:w-full sm:h-full lazyload text-transparent light-gallery cursor-zoom-in border-frame dark:border-secondary-200 `}
                              classList={{
                                hidden: !isSelected(),
                                lazypreload: index() < 3,
                              }}
                              innerHTML={media.tag}
                            />
                          </div>
                        </Match>
                      </Switch>
                    );
                  }}
                </For>
              </div>
            );
          }}
        </For>

        <div
          ref={prevBtnRef}
          class="group absolute w-1/6 md:w-1/5 sm:landscape:h-full pl-0 text-lg z-20 text-outstroke items-center justify-start flex cursor-pointer"
          classList={{
            hidden: !prevBtnVisibility(),
            "bottom-1/2 md:bottom-0 md:top-0 p-0 left-0": heroSectionSettings.isNewVersion,
            "bottom-2 p-2 left-1": !heroSectionSettings.isNewVersion
          }}
          onclick={() => variantScroll("left")}
        >
          <svg
            aria-label="Pan Left"
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            class="rotate-180"
          >
            <path d="M8 0L24 16L8 32V0Z" class="fill-canvas" />
            <path
              d="M24 16L8 32V0L24 16ZM9 29.5859L22.5859 16L9 2.41406V29.5859Z"
              class="fill-frame"
            />
          </svg>
        </div>
        <div
          ref={nextBtnRef}
          class="group cursor-pointer absolute w-1/6 md:w-1/6 sm:landscape:h-full pr-0 text-lg z-20 text-outstroke items-center justify-end flex"
          classList={{
            "bottom-1/2 md:bottom-0 md:top-0 p-0 right-0": heroSectionSettings.isNewVersion,
            "bottom-2 p-2 right-1": !heroSectionSettings.isNewVersion
          }}
          onclick={() => variantScroll("right")}
        >
          <svg
            aria-label="Pan Right"
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
          >
            <path d="M8 0L24 16L8 32V0Z" class="fill-canvas" />
            <path
              d="M24 16L8 32V0L24 16ZM9 29.5859L22.5859 16L9 2.41406V29.5859Z"
              class="fill-frame"
            />
          </svg>
        </div>
      </div>
      <Show when={!heroSectionSettings.isNewVersion}>
        <div class="bg-canvas-depressed sm:sticky sm:bottom-0 border-b border-frame">
          <For each={colorOptions()}>
            {(colorOption, index) => {
              const isSelected = () => isColorSelected(colorOption.id);
              const galleryMedia = () => colorOption.media;
              return (
                <div
                  class="flex justify-center   sm:items-center sm:pt-1 sm:pb-0.75 gap-1 overflow-x-auto"
                  classList={{
                    hidden: !isSelected(),
                  }}
                >
                  <HorizontalScrollerGradient
                    wrapper="ul"
                    wrapperClass={`horizontal-scroller-wrapper flex sm:p-0 max-w-full`} // scrollbar-hide
                    parentClass="items-start"
                    slides={[
                      ...galleryMedia().map((media, index) => {
                        const image =
                          media.type == "image" ? media : media.previewImage;
                        const gridSpan = getGridSpan(
                          index,
                          galleryMedia().length
                        );
                        return {
                          component: () => (
                            <div
                              href={`#${colorOption.id}-slide-${index}`}
                              onClick={() => {
                                document
                                  .querySelector(
                                    `#${colorOption.id}-slide-${index}`
                                  )
                                  ?.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
                                window.posthog?.capture(
                                  "pdp_gallery_thumbnail_clicks",
                                  {
                                    experiment_flags: window.experimentFlags,
                                  }
                                );
                              }}
                              class={`${gridSpan} block cursor-pointer shrink-0 w-8 aspect-[4/5]  snap-start snap-always scroll-m-0 scroll-p-0 ${props?.isModal
                                ? "pr-0.5 lg:pr-0 lg:pb-3 landscape:pb-1 landscape:pr-0"
                                : "dark:border-secondary-200  border-frame border-b-outer sm:border-b-0 border-l-outer first:border-l-0 "
                                }`}
                              data-pswp-src={image.src}
                              data-pswp-width={image.width}
                              data-pswp-height={image.height}
                            >
                              <img
                                class={`${gridSpan} object-cover object-center w-full h-full  lazyload text-transparent  light-gallery cursor-pointer border-frame dark:border-secondary-200 `}
                                classList={{
                                  hidden: !isSelected(),
                                  lazypreload: isSelected() && index < 3,
                                }}
                                style={`aspect-ratio: ${image.aspectRatio}`}
                                src={isSelected() ? `${image.src}&width=${LQIP_WIDTH}` : undefined}
                                data-aspectratio={image.aspectRatio}
                                data-width={image.width}
                                data-sizes={image.sizes}
                                alt={image.alt}
                                data-srcset={[462, 614, 768]
                                  .map(
                                    (width) =>
                                      image.src + `&width=${width} ${width}w`
                                  )
                                  .join(", ")}
                                height="auto"
                                width="auto"
                                fetchpriority="auto"
                                loading={isSelected() && index < 6 ? "eager" : "lazy"}
                                decoding="async"
                              ></img>
                            </div>
                          ),
                          props: {},
                        };
                      }),
                    ]}
                    scrollMultiplier={0.5}
                  />
                </div>
              );
            }}
          </For>
        </div>
      </Show>
    </div>
  );
}
